import React, { ChangeEvent } from 'react';
import { Box, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, FormLabel, Radio, RadioGroup } from '@mui/material';
import { Uniform } from '../../../../components/uniform/uniform';
import { UniformStepContainer } from '../../../../components/uniform/uniform-step-container';
import { UniformStep } from '../../../../components/uniform/uniform-step';
import { LessonTitlezone } from './lesson-titlezone';
import { LessonLaunchbox } from './lesson-launchbox';
import { Accordion, AccordionSummary, AccordionDetails } from '../../../../components/accordion';
import { Row } from '../../../../components/row';
import { Cell } from '../../../../components/cell';
import { ReadonlyInput } from '../../../../components/readonly-input';
import { DatePicker, DatePickerValue } from '../../../../components/date-picker';
import { TimePicker, TimePickerValue } from '../../../../components/time-picker';
import { SingleSelect } from '../../../../components/single-select';
import { RadioOption } from '../../../../components/radio-group';
import { YesNo } from '../../../../components/radio-group/yes-no-component';
import { formatDate, formatTimeHHMM, toDate } from '../../../../utilities';
import { ILesson, ILessonErrors } from '../../interfaces';
import { CitizenLessonAttendanceModel, ClassroomDto, EducatorDto, LessonStateDto } from '../../../../api-service/clients';
import { formatTime, isAttendanceSheetLocked, toTime } from '../../utils';
import { MultiSelect } from '../../../../components/multi-select';
import { getLessonProtocolPath } from '../../attendance-area-pathnames';
import { FormHelperText } from '../../../../components/form-helper-text';
import { CitizensOnLessonTable } from '../components/citizens-on-lesson-table';
import { ResponseTimeMarker } from '../../../../apm/response-time-measurement';

interface ILessonComponentProps {
  lesson: ILesson;
  errors: ILessonErrors;
  isEditmode: boolean;
  isLoading: boolean;
  onCreateLessonClick: () => void;
  onCreateAndPublishLessonClick: () => void;
  onUpdateLessonClick: () => void;
  onCancelLesson: () => void;
  onPublishLesson: () => void;
  onDeleteLesson: () => void;
  onValidate: () => boolean;
  onStringChange: (name: string, value?: string) => void;
  onYesNoChange: (name: string, value: YesNo) => void;
  onDateChange: (name: string, value: DatePickerValue) => void;
  onTimeChange: (name: string, value: TimePickerValue) => void;
  onTimeToDateChange: (name: string, value: TimePickerValue) => void;
  onCheckboxChange: (name: string, checked: boolean) => void;
  onEducatorsChange: (value?: EducatorDto[]) => void;
  onAttendanceCheckStartChange: (name: string, value: YesNo) => void;
  onAttendanceCheckEndChange: (name: string, value: YesNo) => void;
  classRooms: ClassroomDto[];
  educators: EducatorDto[];
  citizensOnLesson: CitizenLessonAttendanceModel[];
  restrictedCitizenCount: number;
  isCourseCompleted: boolean;
  clearWeekdaysErrors: () => void;
  canSeeOrEditProtocol: boolean;
  hasAttendanceWrite: boolean;
}

export function LessonComponent(props: ILessonComponentProps) {
  const {
    errors,
    lesson,
    isEditmode,
    isLoading,
    onCreateLessonClick,
    onCreateAndPublishLessonClick,
    onUpdateLessonClick,
    onCancelLesson,
    onPublishLesson,
    onDeleteLesson,
    onValidate,
    onStringChange,
    onYesNoChange,
    onDateChange,
    onTimeChange,
    onTimeToDateChange,
    onCheckboxChange,
    onEducatorsChange,
    onAttendanceCheckStartChange,
    onAttendanceCheckEndChange,
    classRooms,
    educators,
    citizensOnLesson,
    restrictedCitizenCount,
    isCourseCompleted,
    clearWeekdaysErrors,
    canSeeOrEditProtocol,
    hasAttendanceWrite,
  } = props;
  const {
    lessonId,
    courseName,
    startDate,
    startTime,
    endTime,
    educatorIds,
    classroomId,

    attendanceCheckStart,
    earliestArrivalOnTimeBeforeStart,
    latestArrivalOnTimeAfterStart,
    latestAttendanceRegistrationAfterStart,

    attendanceCheckEnd,
    earliestDepartureRegistrationBeforeEnd,
    earliestDepartureOnTimeBeforeEnd,
    latestDepartureOnTimeAfterEnd,

    isReoccuringLesson,
    reoccuringLessonStartDate,
    reoccuringLessonEndDate,
    lessonState,
    attendanceSheetState,
  } = lesson;

  function handleAttendanceCheckStartChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newValue = (event.target as HTMLInputElement).value as YesNo;
    onAttendanceCheckStartChange(event.target.name, newValue);
  }

  function handleAttendanceCheckEndChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newValue = (event.target as HTMLInputElement).value as YesNo;
    onAttendanceCheckEndChange(event.target.name, newValue);
  }

  function handleYesNoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newValue = (event.target as HTMLInputElement).value as YesNo;
    onYesNoChange(event.target.name, newValue);
  }

  function handleCheckboxChange(event: ChangeEvent<HTMLInputElement>, checked: boolean): void {
    clearWeekdaysErrors();
    onCheckboxChange(event.target.name, checked);
  }

  const isCancelled = lessonState === LessonStateDto.Cancelled;
  const isReadOnly = isCancelled || isCourseCompleted || isAttendanceSheetLocked(attendanceSheetState) || !hasAttendanceWrite;
  const canOpenProtocol = !isCancelled && !isCourseCompleted && canSeeOrEditProtocol;

  function handleOpenProtocol() {
    if (lessonId) {
      window.open(`/attendance/${getLessonProtocolPath(lessonId)}`);
    }
  }

  return (
    <>
      <ResponseTimeMarker measurement="FM_vislektion" isReady={isLoading === false} />
      <Uniform
        titleZone={<LessonTitlezone isEditMode={isEditmode} lessonState={lessonState} />}
        launchbox={
          <LessonLaunchbox
            isLoading={isLoading}
            onSave={onUpdateLessonClick}
            onValidate={onValidate}
            onCancelLesson={onCancelLesson}
            isReadOnly={isReadOnly}
            canOpenProtocol={canOpenProtocol}
            onOpenProtocol={handleOpenProtocol}
            onDeleteLesson={onDeleteLesson}
            lessonState={lesson.lessonState}
            onPublishLesson={onPublishLesson}
          />
        }
        isEditMode={isEditmode}
        showButtons={!isEditmode}
        onOk={onCreateAndPublishLessonClick}
        onCancel={() => window.close()}
        okButtonIsLoading={isLoading}
        showSpinnerOnNextAndComplete={isLoading}
      >
        <UniformStep
          stepName="Generelt"
          validate={onValidate}
          extraButtons={[
            {
              onClick: onCreateLessonClick,
              title: 'Gem som kladde',
            },
          ]}
        >
          <UniformStepContainer>
            <Accordion defaultExpanded>
              <AccordionSummary>Om lektion</AccordionSummary>
              <AccordionDetails>
                <Box mt={1}>
                  <Row>
                    <Cell size="full">
                      <ReadonlyInput value={courseName} label="Hold" />
                    </Cell>
                  </Row>
                  {!isEditmode && (
                    <Row>
                      <Cell size="full">
                        <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                          <FormLabel id="active-radios" component="legend">
                            Gentagelser
                          </FormLabel>
                          <RadioGroup aria-labelledby="active-radios" row name="isReoccuringLesson" value={isReoccuringLesson ? YesNo.Yes : YesNo.No} onChange={handleYesNoChange}>
                            <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                          </RadioGroup>
                        </FormControl>
                      </Cell>
                    </Row>
                  )}
                  {isReoccuringLesson && (
                    <>
                      <Row>
                        <Cell size="quarters">
                          {!isReadOnly ? (
                            <DatePicker
                              label="Fra dato"
                              fullWidth
                              onChange={(date: DatePickerValue) => onDateChange('reoccuringLessonStartDate', date)}
                              value={reoccuringLessonStartDate}
                              variant="outlined"
                              minDate={new Date()}
                              required
                              error={!!errors.reoccuringLessonStartDate}
                              errorText={errors.reoccuringLessonStartDate}
                            />
                          ) : (
                            <ReadonlyInput label="Fra dato" value={formatDate(reoccuringLessonStartDate)} />
                          )}
                        </Cell>
                        <Cell size="quarters">
                          {!isReadOnly ? (
                            <DatePicker
                              label="Til dato"
                              fullWidth
                              onChange={(date: DatePickerValue) => onDateChange('reoccuringLessonEndDate', date)}
                              value={reoccuringLessonEndDate}
                              minDate={toDate(reoccuringLessonStartDate) || toDate(startDate) || new Date()}
                              variant="outlined"
                              required
                              error={!!errors.reoccuringLessonEndDate}
                              errorText={errors.reoccuringLessonEndDate}
                            />
                          ) : (
                            <ReadonlyInput label="Til dato" value={formatDate(reoccuringLessonEndDate)} />
                          )}
                        </Cell>
                        <Cell size="quarters">
                          {!isReadOnly ? (
                            <TimePicker
                              fullWidth
                              onChange={(date: TimePickerValue) => onTimeChange('startTime', date)}
                              value={startTime}
                              variant="outlined"
                              required
                              label="Starttidspunkt"
                              error={!!errors.startTime}
                              errorText={errors.startTime}
                            />
                          ) : (
                            <ReadonlyInput label="Starttidspunkt" value={formatTimeHHMM(startTime)} />
                          )}
                        </Cell>
                        <Cell size="quarters">
                          {!isReadOnly ? (
                            <TimePicker
                              fullWidth
                              onChange={(date: TimePickerValue) => onTimeChange('endTime', date)}
                              value={endTime}
                              variant="outlined"
                              required
                              label="Sluttidspunkt"
                              error={!!errors.endTime}
                              errorText={errors.endTime}
                            />
                          ) : (
                            <ReadonlyInput label="Sluttidspunkt" value={formatTimeHHMM(endTime)} />
                          )}
                        </Cell>
                      </Row>
                      <Row>
                        <Cell size="half">
                          <FormControl component="fieldset" variant="standard" error={!!errors.weekdays}>
                            <FormLabel component="legend">Hvilke dage skal lektionen gentages?</FormLabel>
                            <FormGroup>
                              <Box>
                                <FormControlLabel control={<Checkbox name="monday" onChange={handleCheckboxChange} />} label="Mandag" />
                                <FormControlLabel control={<Checkbox name="tuesday" onChange={handleCheckboxChange} />} label="Tirsdag" />
                                <FormControlLabel control={<Checkbox name="wednesday" onChange={handleCheckboxChange} />} label="Onsdag" />
                                <FormControlLabel control={<Checkbox name="thursday" onChange={handleCheckboxChange} />} label="Torsdag" />
                                <FormControlLabel control={<Checkbox name="friday" onChange={handleCheckboxChange} />} label="Fredag" />
                              </Box>
                            </FormGroup>
                            {errors.weekdays && <FormHelperText>{errors.weekdays}</FormHelperText>}
                          </FormControl>
                        </Cell>
                      </Row>
                    </>
                  )}
                  {!isReoccuringLesson && (
                    <Row>
                      <Cell size="half">
                        {!isReadOnly ? (
                          <DatePicker
                            label="Dato"
                            fullWidth
                            onChange={(date: DatePickerValue) => onDateChange('startDate', date)}
                            value={startDate}
                            minDate={new Date()}
                            variant="outlined"
                            required
                            error={!!errors.startDate}
                            errorText={errors.startDate}
                          />
                        ) : (
                          <ReadonlyInput label="Dato" value={formatDate(startDate)} />
                        )}
                      </Cell>
                      <Cell size="quarters">
                        {!isReadOnly ? (
                          <TimePicker
                            fullWidth
                            onChange={(date: TimePickerValue) => onTimeChange('startTime', date)}
                            value={startTime}
                            variant="outlined"
                            required
                            label="Starttidspunkt"
                            error={!!errors.startTime}
                            errorText={errors.startTime}
                          />
                        ) : (
                          <ReadonlyInput label="Starttidspunkt" value={formatTimeHHMM(startTime)} />
                        )}
                      </Cell>
                      <Cell size="quarters">
                        {!isReadOnly ? (
                          <TimePicker
                            fullWidth
                            onChange={(date: TimePickerValue) => onTimeChange('endTime', date)}
                            value={endTime}
                            variant="outlined"
                            required
                            label="Sluttidspunkt"
                            error={!!errors.endTime}
                            errorText={errors.endTime}
                          />
                        ) : (
                          <ReadonlyInput label="Sluttidspunkt" value={formatTimeHHMM(endTime)} />
                        )}
                      </Cell>
                    </Row>
                  )}
                  {isReoccuringLesson && (
                    <Row>
                      <Cell size="full">
                        <Divider />
                      </Cell>
                    </Row>
                  )}
                  <Row>
                    <Cell size="half">
                      {!isReadOnly ? (
                        <MultiSelect<EducatorDto>
                          label="Undervisere"
                          value={educators.filter(x => educatorIds?.includes(x.educatorId))}
                          options={educators}
                          getOptionLabel={x => x.name ?? ''}
                          renderOption={(prop: object, option: EducatorDto) => (
                            <li {...prop} key={option.educatorId}>
                              {option.name}
                            </li>
                          )}
                          onChange={(value?: EducatorDto[]) => onEducatorsChange(value)}
                          variant="outlined"
                        />
                      ) : (
                        <ReadonlyInput
                          value={educators
                            .filter(x => educatorIds?.includes(x.educatorId))
                            .map(x => x.name)
                            .join(', ')}
                          label="Undervisere"
                        />
                      )}
                    </Cell>
                    <Cell size="half">
                      {!isReadOnly ? (
                        <SingleSelect<ClassroomDto>
                          label="Lokale"
                          placeholder="Fremsøg lokale"
                          value={classRooms.find(x => x.classroomId === classroomId)}
                          options={classRooms}
                          getOptionLabel={x => x.name}
                          filterOptions={(options, state) => options.filter(x => x.isActive === true && x.name.toLowerCase().includes(state.inputValue.toLocaleLowerCase()))}
                          renderOption={(prop: object, option: ClassroomDto) => (
                            <li {...prop} key={option.classroomId}>
                              {option.name}
                            </li>
                          )}
                          onChange={(value?: ClassroomDto) => onStringChange('classroomId', value?.classroomId)}
                          variant="outlined"
                          error={!!errors.classroomId}
                          errorText={errors.classroomId}
                        />
                      ) : (
                        <ReadonlyInput label="Lokale" value={classRooms.find(x => x.classroomId === classroomId)?.name} />
                      )}
                    </Cell>
                  </Row>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded>
              <AccordionSummary>Fremmødetjek</AccordionSummary>
              <AccordionDetails>
                <Box mt={1}>
                  <Row>
                    <Cell size="full">
                      <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                        <FormLabel id="active-radios" component="legend">
                          Fremmødetjek ved lektions start
                        </FormLabel>
                        {!isReadOnly ? (
                          <RadioGroup
                            aria-labelledby="active-radios"
                            row
                            name="attendanceCheckStart"
                            value={attendanceCheckStart ? YesNo.Yes : YesNo.No}
                            onChange={handleAttendanceCheckStartChange}
                          >
                            <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                          </RadioGroup>
                        ) : (
                          <Box>{attendanceCheckStart ? 'Ja' : 'Nej'}</Box>
                        )}
                      </FormControl>
                    </Cell>
                  </Row>
                  {attendanceCheckStart && (
                    <>
                      <Row>
                        <Cell size="half">
                          <ReadonlyInput label="Tidligste registrering af fremmøde (tidspunkt)" value={`${formatTime(earliestArrivalOnTimeBeforeStart) || ''}`} />
                        </Cell>
                        <Cell size="half">
                          {!isReadOnly ? (
                            <TimePicker
                              fullWidth
                              variant="outlined"
                              label="Tidligst til tiden (tidspunkt)"
                              required
                              value={toTime(earliestArrivalOnTimeBeforeStart)}
                              onChange={(value: TimePickerValue) => onTimeToDateChange('earliestArrivalOnTimeBeforeStart', value)}
                              error={!!errors.earliestArrivalOnTimeBeforeStart}
                              errorText={errors.earliestArrivalOnTimeBeforeStart}
                            />
                          ) : (
                            <ReadonlyInput label="Tidligst til tiden (tidspunkt)" value={formatTime(earliestArrivalOnTimeBeforeStart)} />
                          )}
                        </Cell>
                      </Row>
                      <Row>
                        <Cell size="half">
                          {!isReadOnly ? (
                            <TimePicker
                              fullWidth
                              variant="outlined"
                              label="Senest til tiden: (tidspunkt)"
                              required
                              value={toTime(latestArrivalOnTimeAfterStart)}
                              onChange={(value: TimePickerValue) => onTimeToDateChange('latestArrivalOnTimeAfterStart', value)}
                              error={!!errors.latestArrivalOnTimeAfterStart}
                              errorText={errors.latestArrivalOnTimeAfterStart}
                            />
                          ) : (
                            <ReadonlyInput label="Senest til tiden: (tidspunkt)" value={formatTime(latestArrivalOnTimeAfterStart)} />
                          )}
                        </Cell>
                        <Cell size="half">
                          {!isReadOnly ? (
                            <TimePicker
                              fullWidth
                              variant="outlined"
                              label="Seneste registrering af fremmøde (tidspunkt)"
                              required
                              value={toTime(latestAttendanceRegistrationAfterStart)}
                              onChange={(value: TimePickerValue) => onTimeToDateChange('latestAttendanceRegistrationAfterStart', value)}
                              error={!!errors.latestAttendanceRegistrationAfterStart}
                              errorText={errors.latestAttendanceRegistrationAfterStart}
                            />
                          ) : (
                            <ReadonlyInput label="Seneste registrering af fremmøde (tidspunkt)" value={formatTime(latestAttendanceRegistrationAfterStart)} />
                          )}
                        </Cell>
                      </Row>
                    </>
                  )}
                  <Row>
                    <Cell size="full">
                      <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                        <FormLabel id="active-radios" component="legend">
                          Fremmødetjek ved lektions slut
                        </FormLabel>
                        {!isReadOnly ? (
                          <RadioGroup
                            aria-labelledby="active-radios"
                            row
                            name="attendanceCheckEnd"
                            value={attendanceCheckEnd ? YesNo.Yes : YesNo.No}
                            onChange={handleAttendanceCheckEndChange}
                          >
                            <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                          </RadioGroup>
                        ) : (
                          <Box>{attendanceCheckEnd ? 'Ja' : 'Nej'}</Box>
                        )}
                      </FormControl>
                    </Cell>
                  </Row>
                  {attendanceCheckEnd && (
                    <>
                      <Row>
                        <Cell size="half">
                          {!isReadOnly ? (
                            <TimePicker
                              fullWidth
                              variant="outlined"
                              label="Tidligste registrering af afgang (tidspunkt)"
                              required
                              value={toTime(earliestDepartureRegistrationBeforeEnd)}
                              onChange={(value: TimePickerValue) => onTimeToDateChange('earliestDepartureRegistrationBeforeEnd', value)}
                              error={!!errors.earliestDepartureRegistrationBeforeEnd}
                              errorText={errors.earliestDepartureRegistrationBeforeEnd}
                            />
                          ) : (
                            <ReadonlyInput label="Tidligste registrering af afgang (tidspunkt)" value={formatTime(earliestDepartureRegistrationBeforeEnd)} />
                          )}
                        </Cell>
                        <Cell size="half">
                          {!isReadOnly ? (
                            <TimePicker
                              fullWidth
                              variant="outlined"
                              label="Tidligst til tiden (tidspunkt)"
                              required
                              value={toTime(earliestDepartureOnTimeBeforeEnd)}
                              onChange={(value: TimePickerValue) => onTimeToDateChange('earliestDepartureOnTimeBeforeEnd', value)}
                              error={!!errors.earliestDepartureOnTimeBeforeEnd}
                              errorText={errors.earliestDepartureOnTimeBeforeEnd}
                            />
                          ) : (
                            <ReadonlyInput label="Tidligst til tiden (tidspunkt)" value={formatTime(earliestDepartureOnTimeBeforeEnd)} />
                          )}
                        </Cell>
                      </Row>
                      <Row>
                        <Cell size="half">
                          {!isReadOnly ? (
                            <TimePicker
                              fullWidth
                              variant="outlined"
                              label="Senest til tiden: (tidspunkt)"
                              required
                              value={toTime(latestDepartureOnTimeAfterEnd)}
                              onChange={(value: TimePickerValue) => onTimeToDateChange('latestDepartureOnTimeAfterEnd', value)}
                              error={!!errors.latestDepartureOnTimeAfterEnd}
                              errorText={errors.latestDepartureOnTimeAfterEnd}
                            />
                          ) : (
                            <ReadonlyInput label="Senest til tiden: (tidspunkt)" value={formatTime(latestDepartureOnTimeAfterEnd)} />
                          )}
                        </Cell>
                        <Cell size="half">
                          <ReadonlyInput label="Seneste registrering af afgang (tidspunkt)" value={`${formatTime(latestDepartureOnTimeAfterEnd) || ''}`} />
                        </Cell>
                      </Row>
                    </>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
            {isEditmode && canSeeOrEditProtocol && (
              <Accordion defaultExpanded>
                <AccordionSummary>Borgere på lektionen</AccordionSummary>
                <AccordionDetails>
                  <Box mt={1}>
                    {citizensOnLesson.length === 0 && restrictedCitizenCount === 0 ? (
                      <Box sx={{ color: 'text.secondary' }}>Der er ingen borgere på lektionen</Box>
                    ) : (
                      <CitizensOnLessonTable citizensOnLesson={citizensOnLesson} restrictedCitizenCount={restrictedCitizenCount} />
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </UniformStepContainer>
        </UniformStep>
      </Uniform>
    </>
  );
}
